import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './Home.css';
import './ProductsActions.css';
import DateFilter from '../DateFilter';
import dateUtils from '../../utils/dateUtils';
import apiService from '../../services/api';
import { useNavigate } from 'react-router-dom';
import PopupModal from '../PopupModal';
import AddPaymentModal from './AddPaymentModal';
import ViewSaleModal from './ViewSaleModal';
import EditShippingModal from './EditShippingModal';
import SendNotificationModal from './SendNotificationModal';
import LedgerModal from './LedgerModal';
import ViewPurchaseModal, { printPurchaseOrder } from './ViewPurchaseModal';
import {
  Banknote, Search, Eye, TrendingUp, DollarSign, Activity, FileText,
  RotateCcw, ShoppingBag, Clock, Receipt, CreditCard, Sparkles,
  MapPin, Calendar, AlertTriangle, ArrowUpRight, CheckCircle2, ShieldAlert, Loader2
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

import { useBusinessSettings } from '../../contexts/BusinessSettingsContext';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';

const getYesterdayRange = () => {
  const today = dateUtils.getChicagoToday ? dateUtils.getChicagoToday() : new Date();
  const start = new Date(today);
  start.setDate(today.getDate() - 1);
  start.setHours(0, 0, 0, 0);

  const end = new Date(today);
  end.setDate(today.getDate() - 1);
  end.setHours(23, 59, 59, 999);

  return { start, end };
};

const Home = ({ currentUser }) => {
  const navigate = useNavigate();
  const { formatCurrency, formatCurrencyNumber, formatDate, formatDateTime, quantityPrecision } = useBusinessSettings();
  const [selectedLocationId, setSelectedLocationId] = useState('all');

  const [locations, setLocations] = useState([]);
  const [dateRange, setDateRange] = useState(getYesterdayRange);

  const [totalsData, setTotalsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salesChartData, setSalesChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(true);

  const [fyChartData, setFyChartData] = useState([]);
  const [fyChartLoading, setFyChartLoading] = useState(true);
  const [salesDues, setSalesDues] = useState([]);
  const [purchaseDues, setPurchaseDues] = useState([]);
  const [duesLoading, setDuesLoading] = useState(true);

  const [salesDuesLocation, setSalesDuesLocation] = useState('all');
  const [purchaseDuesLocation, setPurchaseDuesLocation] = useState('all');
  const [chequePaymentsLocation, setChequePaymentsLocation] = useState('all');

  const [salesPage, setSalesPage] = useState(1);
  const [purchasePage, setPurchasePage] = useState(1);
  const [stockPage, setStockPage] = useState(1);
  const itemsPerPage = 10;

  const [stockAlerts, setStockAlerts] = useState([]);
  const [stockAlertLoading, setStockAlertLoading] = useState(true);
  const [stockAlertLocation, setStockAlertLocation] = useState('all');

  const [salesSearch, setSalesSearch] = useState('');
  const [purchaseSearch, setPurchaseSearch] = useState('');
  const [stockSearch, setStockSearch] = useState('');
  const [chequePaymentsSearch, setChequePaymentsSearch] = useState('');

  const [chequePayments, setChequePayments] = useState([]);
  const [chequePaymentsLoading, setChequePaymentsLoading] = useState(true);
  const [chequePaymentsPage, setChequePaymentsPage] = useState(1);
  const [editingChequeId, setEditingChequeId] = useState(null);
  const [viewSaleModal, setViewSaleModal] = useState({ open: false, sale: null, loading: false });
  const [viewPurchaseModal, setViewPurchaseModal] = useState({ open: false, purchase: null, loading: false });
  const [viewPurchaseReqModal, setViewPurchaseReqModal] = useState({ open: false, data: null, loading: false });
  const [editShippingModal, setEditShippingModal] = useState({ open: false, sale: null });
  const [notificationModal, setNotificationModal] = useState({ open: false, sale: null });
  const [ledgerModal, setLedgerModal] = useState({ open: false, sale: null });

  const handleViewPurchaseReq = async (req) => {
    setOpenActionId(null);
    setViewPurchaseReqModal({ open: true, data: req, loading: true });
    try {
      const res = await apiService.getPurchaseRequisition(req.id);
      setViewPurchaseReqModal({ open: true, data: res.data || res || req, loading: false });
    } catch (err) {
      setViewPurchaseReqModal({ open: true, data: req, loading: false });
    }
  };

  const handleDeletePurchaseReq = async (id) => {
    setOpenActionId(null);
    if (!window.confirm('Are you sure you want to delete this purchase requisition?')) return;
    try {
      await apiService.deletePurchaseRequisition(id);
      fetchPurchaseReqs();
    } catch (err) {
      /* ignore */
    }
  };

  const resolveRequiredByDate = (r) => {
    if (!r) return '--';
    const dateVal = r.delivery_date || r.required_by_date || r.due_date || r.delivery_date_formatted || r.required_by_date_formatted;
    if (!dateVal) return '--';
    return formatDateTime(dateVal);
  };

  const resolveAddedBy = (r) => {
    if (!r) return '--';
    if (typeof r.added_by === 'string' && r.added_by.trim()) return r.added_by;
    if (typeof r.created_by_name === 'string' && r.created_by_name.trim()) return r.created_by_name;
    if (typeof r.requested_by === 'string' && r.requested_by.trim()) return r.requested_by;
    if (typeof r.user_full_name === 'string' && r.user_full_name.trim()) return r.user_full_name;
    if (r.created_by_user) {
      const u = r.created_by_user;
      const fullName = [u.surname, u.first_name, u.last_name].filter(Boolean).join(' ');
      if (fullName) return fullName;
      if (u.username) return u.username;
    }
    if (typeof r.created_by === 'string' && r.created_by.trim()) return r.created_by;
    return '--';
  };

  const resolveSupplierName = (r) => {
    if (!r) return '--';
    return (
      r.supplier_business_name ||
      r.contact?.supplier_business_name ||
      r.contact?.name ||
      r.supplier?.name ||
      r.supplier?.supplier_business_name ||
      r.supplier_name ||
      r.contact_name ||
      (typeof r.supplier === 'string' ? r.supplier : '') ||
      (typeof r.contact === 'string' ? r.contact : '') ||
      (r.name && r.name !== r.ref_no ? r.name : '') ||
      '--'
    );
  };

  const handleViewPO = async (po) => {
    setOpenActionId(null);
    setViewPurchaseModal({ open: true, purchase: po, loading: true });
    try {
      const res = await apiService.getPurchaseOrder(po.id);
      setViewPurchaseModal({ open: true, purchase: res.data || res || po, loading: false });
    } catch (err) {
      setViewPurchaseModal({ open: true, purchase: po, loading: false });
    }
  };

  const handlePrintPO = async (po) => {
    setOpenActionId(null);
    try {
      if (po.purchase_lines && po.purchase_lines.length > 0) {
        printPurchaseOrder(po);
      } else {
        const res = await apiService.getPurchaseOrder(po.id);
        printPurchaseOrder(res.data || res || po);
      }
    } catch (err) {
      printPurchaseOrder(po);
    }
  };

  const handleDownloadPOPdf = async (po) => {
    setOpenActionId(null);
    try {
      await apiService.downloadPurchaseOrderPdf(po.id);
    } catch (err) {
      /* ignore */
    }
  };

  const handleDeletePO = async (poId) => {
    setOpenActionId(null);
    if (!window.confirm('Are you sure you want to delete this purchase order?')) return;
    try {
      await apiService.deletePurchaseOrder(poId);
      if (apiService.isTokenValid()) {
        const params = { per_page: -1 };
        if (purchaseOrdersLocation !== 'all') params.location_id = purchaseOrdersLocation;
        const res = await apiService.getPurchaseOrders(params);
        setPurchaseOrders(safeArray(res));
      }
    } catch (err) {
      /* ignore */
    }
  };

  const [paymentModal, setPaymentModal] = useState({ open: false, type: null, row: null });

  // 4 New Dashboard Tables State
  const [salesOrders, setSalesOrders] = useState([]);
  const [salesOrdersLoading, setSalesOrdersLoading] = useState(true);
  const [salesOrdersLocation, setSalesOrdersLocation] = useState('all');
  const [salesOrdersSearch, setSalesOrdersSearch] = useState('');
  const [salesOrdersPage, setSalesOrdersPage] = useState(1);

  const [purchaseReqs, setPurchaseReqs] = useState([]);
  const [purchaseReqLoading, setPurchaseReqLoading] = useState(true);
  const [purchaseReqLocation, setPurchaseReqLocation] = useState('all');
  const [purchaseReqSearch, setPurchaseReqSearch] = useState('');
  const [purchaseReqPage, setPurchaseReqPage] = useState(1);

  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [purchaseOrdersLoading, setPurchaseOrdersLoading] = useState(true);
  const [purchaseOrdersLocation, setPurchaseOrdersLocation] = useState('all');
  const [purchaseOrdersSearch, setPurchaseOrdersSearch] = useState('');
  const [purchaseOrdersPage, setPurchaseOrdersPage] = useState(1);

  const [pendingShipments, setPendingShipments] = useState([]);
  const [pendingShipmentsLoading, setPendingShipmentsLoading] = useState(true);
  const [pendingShipmentsLocation, setPendingShipmentsLocation] = useState('all');
  const [pendingShipmentsSearch, setPendingShipmentsSearch] = useState('');
  const [pendingShipmentsPage, setPendingShipmentsPage] = useState(1);

  const [openActionId, setOpenActionId] = useState(null);
  const [openExportDropdown, setOpenExportDropdown] = useState(null);
  const [showPopup, setShowPopup] = useState(false);

  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, openUpwards: false });

  const toggleActionMenu = (id, event) => {
    if (openActionId === id) {
      setOpenActionId(null);
    } else {
      if (event && event.currentTarget) {
        const rect = event.currentTarget.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUpwards = spaceBelow < 300 && rect.top > 300;
        setDropdownPosition({
          top: openUpwards ? (rect.top - 6) : (rect.bottom + 4),
          left: Math.min(rect.left, window.innerWidth - 210),
          openUpwards
        });
      }
      setOpenActionId(id);
    }
  };

  useEffect(() => {
    if (!openActionId && !openExportDropdown) return;
    const handleClickOutside = (e) => {
      if (openActionId && !e.target.closest('.action-dropdown-container') && !e.target.closest('.action-btn-trigger') && !e.target.closest('.action-dropdown-menu')) {
        setOpenActionId(null);
      }
      if (openExportDropdown && !e.target.closest('.export-dropdown-container')) {
        setOpenExportDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openActionId, openExportDropdown]);

  const handleTopLocationChange = (locId) => {
    setSelectedLocationId(locId);
    setSalesDuesLocation(locId);
    setPurchaseDuesLocation(locId);
    setStockAlertLocation(locId);
    setChequePaymentsLocation(locId);
    setSalesOrdersLocation(locId);
    setPurchaseReqLocation(locId);
    setPurchaseOrdersLocation(locId);
    setPendingShipmentsLocation(locId);
  };

  const [locationsLoaded, setLocationsLoaded] = useState(false);

  // Fetch locations
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await apiService.getLocations();
        const locList = response.data || response || [];
        setLocations(locList);
        if (locList.length > 0) {
          const userLoc = currentUser?.location_id || currentUser?.business_location_id;
          if (userLoc && locList.some(l => String(l.id) === String(userLoc))) {
            handleTopLocationChange(String(userLoc));
          } else if (locList.length === 1) {
            handleTopLocationChange(String(locList[0].id));
          }
        }
      } catch (error) {
        /* console.error('Error fetching locations:', error); */
      } finally {
        setLocationsLoaded(true);
      }
    };
    fetchLocations();
  }, [currentUser]);

  // Fetch sales last 30 days chart data
  useEffect(() => {
    const fetchSalesChart = async () => {
      if (!apiService.isTokenValid()) return;
      try {
        setChartLoading(true);
        const response = await apiService.getSalesLast30Days();
        const data = response?.data || response || {};
        const labels = data.labels || [];
        const totals = data.total_sales || [];
        const mapped = labels.map((label, i) => ({
          name: label,
          sales: parseFloat(totals[i] || 0)
        }));
        setSalesChartData(mapped);
      } catch (error) {
        /* console.error('Error fetching sales chart:', error); */
        setSalesChartData([]);
      } finally {
        setChartLoading(false);
      }
    };
    fetchSalesChart();
  }, []);

  // Fetch financial-year sales chart data
  useEffect(() => {
    if (!locationsLoaded || !apiService.isTokenValid()) return;
    const fetchFyChart = async () => {
      try {
        setFyChartLoading(true);
        const res = await apiService.getSalesCurrentFinancialYear({
          location_id: selectedLocationId === 'all' ? null : selectedLocationId
        });
        const payload = res?.data?.data || res?.data || {};
        const labels = payload.labels || [];
        const values = payload.values || [];
        const mapped = labels.map((label, i) => ({
          name: label,
          sales: parseFloat(values[i] || 0)
        }));
        setFyChartData(mapped);
      } catch (error) {
        /* console.error('Error fetching FY sales chart:', error); */
        setFyChartData([]);
      } finally {
        setFyChartLoading(false);
      }
    };
    fetchFyChart();
  }, [selectedLocationId, locationsLoaded]);

  // Fetch payment dues tables
  const fetchDuesTables = async (salesLoc = salesDuesLocation, purchaseLoc = purchaseDuesLocation) => {
    if (!apiService.isTokenValid()) return;
    try {
      setDuesLoading(true);
      const [salesRes, purchaseRes] = await Promise.all([
        apiService.getSalesPaymentDues({ location_id: salesLoc === 'all' ? null : salesLoc }),
        apiService.getPurchasePaymentDues({ location_id: purchaseLoc === 'all' ? null : purchaseLoc })
      ]);
      setSalesDues(salesRes?.data || []);
      setPurchaseDues(purchaseRes?.data || []);
    } catch (error) {
      /* console.error('Error fetching dues tables:', error); */
      setSalesDues([]);
      setPurchaseDues([]);
    } finally {
      setDuesLoading(false);
    }
  };

  // Fetch cheque payments table
  const fetchChequePayments = async (location = chequePaymentsLocation) => {
    if (!apiService.isTokenValid()) return;
    try {
      setChequePaymentsLoading(true);
      const res = await apiService.getChequePayments({ location_id: location === 'all' ? null : location });
      setChequePayments(res?.data || []);
    } catch (error) {
      /* console.error('Error fetching cheque payments:', error); */
      setChequePayments([]);
    } finally {
      setChequePaymentsLoading(false);
    }
  };

  useEffect(() => {
    if (locationsLoaded) fetchDuesTables();
  }, [salesDuesLocation, purchaseDuesLocation, locationsLoaded]);

  useEffect(() => {
    if (locationsLoaded) fetchChequePayments();
  }, [chequePaymentsLocation, locationsLoaded]);

  useEffect(() => { setSalesPage(1); }, [salesDuesLocation, salesSearch]);
  useEffect(() => { setPurchasePage(1); }, [purchaseDuesLocation, purchaseSearch]);
  useEffect(() => { setStockPage(1); }, [stockAlertLocation, stockSearch]);
  useEffect(() => { setChequePaymentsPage(1); }, [chequePaymentsLocation, chequePaymentsSearch]);

  useEffect(() => { setSalesOrdersPage(1); }, [salesOrdersLocation, salesOrdersSearch]);
  useEffect(() => { setPurchaseReqPage(1); }, [purchaseReqLocation, purchaseReqSearch]);
  useEffect(() => { setPurchaseOrdersPage(1); }, [purchaseOrdersLocation, purchaseOrdersSearch]);
  useEffect(() => { setPendingShipmentsPage(1); }, [pendingShipmentsLocation, pendingShipmentsSearch]);

  // Helper to extract array safely
  const safeArray = (res) => {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (Array.isArray(res.data)) return res.data;
    if (Array.isArray(res.data?.data)) return res.data.data;
    return [];
  };

  // Fetch Sales Orders
  const fetchSalesOrders = async () => {
    if (!apiService.isTokenValid()) return;
    try {
      setSalesOrdersLoading(true);
      const params = { per_page: 50 };
      if (salesOrdersLocation !== 'all') params.location_id = salesOrdersLocation;
      const res = await apiService.getSalesOrders(params);
      const data = res?.data || (Array.isArray(res) ? res : []);
      setSalesOrders(Array.isArray(data) ? data : []);
    } catch (e) {
      setSalesOrders([]);
    } finally {
      setSalesOrdersLoading(false);
    }
  };

  // Fetch Purchase Requisitions
  const fetchPurchaseReqs = async () => {
    if (!apiService.isTokenValid()) return;
    try {
      setPurchaseReqLoading(true);
      const params = { per_page: 50 };
      if (purchaseReqLocation !== 'all') params.location_id = purchaseReqLocation;
      const res = await apiService.getPurchaseRequisitions(params);
      setPurchaseReqs(safeArray(res));
    } catch (e) {
      setPurchaseReqs([]);
    } finally {
      setPurchaseReqLoading(false);
    }
  };

  // Fetch Purchase Orders
  const fetchPurchaseOrders = async () => {
    if (!apiService.isTokenValid()) return;
    try {
      setPurchaseOrdersLoading(true);
      const params = { per_page: 50 };
      if (purchaseOrdersLocation !== 'all') params.location_id = purchaseOrdersLocation;
      const res = await apiService.getPurchaseOrders(params);
      setPurchaseOrders(safeArray(res));
    } catch (e) {
      setPurchaseOrders([]);
    } finally {
      setPurchaseOrdersLoading(false);
    }
  };

  // Fetch Pending Shipments
  const fetchPendingShipments = async () => {
    if (!apiService.isTokenValid()) return;
    try {
      setPendingShipmentsLoading(true);
      const params = { only_shipments: 1, only_pending_shipments: 1, per_page: 50 };
      if (pendingShipmentsLocation !== 'all') params.location_id = pendingShipmentsLocation;
      const res = await apiService.getSales(params);
      setPendingShipments(safeArray(res));
    } catch (e) {
      setPendingShipments([]);
    } finally {
      setPendingShipmentsLoading(false);
    }
  };

  const reloadAllDashboardTables = () => {
    fetchDuesTables();
    fetchChequePayments();
    fetchSalesOrders();
    fetchPurchaseReqs();
    fetchPurchaseOrders();
    fetchPendingShipments();
  };

  useEffect(() => { if (locationsLoaded) fetchSalesOrders(); }, [salesOrdersLocation, locationsLoaded]);
  useEffect(() => { if (locationsLoaded) fetchPurchaseReqs(); }, [purchaseReqLocation, locationsLoaded]);
  useEffect(() => { if (locationsLoaded) fetchPurchaseOrders(); }, [purchaseOrdersLocation, locationsLoaded]);
  useEffect(() => { if (locationsLoaded) fetchPendingShipments(); }, [pendingShipmentsLocation, locationsLoaded]);

  // Fetch dashboard totals
  useEffect(() => {
    if (!locationsLoaded || !apiService.isTokenValid()) return;
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        const formatForApi = (date) => {
          if (!date) return '';
          const d = new Date(date);
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const params = {
          start: formatForApi(dateRange.start),
          end: formatForApi(dateRange.end),
          location_id: selectedLocationId === 'all' ? null : selectedLocationId
        };

        const data = await apiService.getDashboardTotals(params);
        setTotalsData(data);
      } catch (error) {
        /* console.error('❌ DASHBOARD DEBUG - Totals Error:', error); */
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [dateRange, selectedLocationId, locationsLoaded]);

  const filterRows = (rows, term, locationId = 'all') => {
    let filtered = rows;
    if (locationId && locationId !== 'all') {
      const locStr = String(locationId);
      filtered = filtered.filter(row => {
        const rLoc = row.location_id || row.location?.id;
        return rLoc ? String(rLoc) === locStr : true;
      });
    }
    if (term && term.trim()) {
      const lower = term.toLowerCase();
      filtered = filtered.filter(row =>
        String(row.contact || row.customer || row.product || row.contact?.name || '').toLowerCase().includes(lower) ||
        String(row.reference_no || row.ref_no || row.invoice_no || row.cheque_number || row.location || row.location?.name || '').toLowerCase().includes(lower)
      );
    }
    return filtered;
  };

  const filteredSales = filterRows(salesDues, salesSearch, salesDuesLocation);
  const salesTotalPages = Math.ceil(filteredSales.length / itemsPerPage) || 1;
  const paginatedSales = filteredSales.slice((salesPage - 1) * itemsPerPage, salesPage * itemsPerPage);

  const filteredPurchase = filterRows(purchaseDues, purchaseSearch, purchaseDuesLocation);
  const purchaseTotalPages = Math.ceil(filteredPurchase.length / itemsPerPage) || 1;
  const paginatedPurchase = filteredPurchase.slice((purchasePage - 1) * itemsPerPage, purchasePage * itemsPerPage);

  const filteredStock = filterRows(stockAlerts, stockSearch, stockAlertLocation);
  const stockTotalPages = Math.ceil(filteredStock.length / itemsPerPage) || 1;
  const paginatedStock = filteredStock.slice((stockPage - 1) * itemsPerPage, stockPage * itemsPerPage);

  const filteredChequePayments = filterRows(chequePayments, chequePaymentsSearch, chequePaymentsLocation);
  const chequePaymentsTotalPages = Math.ceil(filteredChequePayments.length / itemsPerPage) || 1;
  const paginatedChequePayments = filteredChequePayments.slice((chequePaymentsPage - 1) * itemsPerPage, chequePaymentsPage * itemsPerPage);

  // 4 New Tables Filtering & Pagination
  const filteredSalesOrders = filterRows(salesOrders, salesOrdersSearch, salesOrdersLocation);
  const salesOrdersTotalPages = Math.ceil(filteredSalesOrders.length / itemsPerPage) || 1;
  const paginatedSalesOrders = filteredSalesOrders.slice((salesOrdersPage - 1) * itemsPerPage, salesOrdersPage * itemsPerPage);

  const filteredPurchaseReqs = filterRows(purchaseReqs, purchaseReqSearch, purchaseReqLocation);
  const purchaseReqTotalPages = Math.ceil(filteredPurchaseReqs.length / itemsPerPage) || 1;
  const paginatedPurchaseReqs = filteredPurchaseReqs.slice((purchaseReqPage - 1) * itemsPerPage, purchaseReqPage * itemsPerPage);

  const filteredPurchaseOrders = filterRows(purchaseOrders, purchaseOrdersSearch, purchaseOrdersLocation);
  const purchaseOrdersTotalPages = Math.ceil(filteredPurchaseOrders.length / itemsPerPage) || 1;
  const paginatedPurchaseOrders = filteredPurchaseOrders.slice((purchaseOrdersPage - 1) * itemsPerPage, purchaseOrdersPage * itemsPerPage);

  const filteredPendingShipments = filterRows(pendingShipments, pendingShipmentsSearch, pendingShipmentsLocation);
  const pendingShipmentsTotalPages = Math.ceil(filteredPendingShipments.length / itemsPerPage) || 1;
  const paginatedPendingShipments = filteredPendingShipments.slice((pendingShipmentsPage - 1) * itemsPerPage, pendingShipmentsPage * itemsPerPage);

  const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 4) {
      pages.push(1, 2, 3, 4, 5, '...', totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
    }

    return (
      <div className="etpos-pagination">
        <button
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className="pg-btn pg-prev"
        >
          Previous
        </button>
        {pages.map((p, i) =>
          p === '...' ? (
            <span key={i} className="pg-ellipsis">...</span>
          ) : (
            <button
              key={i}
              onClick={() => onPageChange(p)}
              className={`pg-btn ${p === currentPage ? 'pg-active' : ''}`}
            >
              {p}
            </button>
          )
        )}
        <button
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className="pg-btn pg-next"
        >
          Next
        </button>
      </div>
    );
  };

  const getSalesMetrics = () => {
    const d = totalsData?.data || totalsData || {};

    const spinner = <svg className="circular-arrow-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /></svg>;

    return [
      {
        title: 'Total Sales',
        value: loading ? spinner : formatCurrency(d.total_sell || 0),
        icon: <DollarSign size={22} />,
        color: 'blue',
        badge: 'Sales',
        gradient: 'linear-gradient(135deg, #3b82f6, #1d4ed8)'
      },
      {
        title: 'Net',
        value: loading ? spinner : formatCurrency(d.net || 0),
        icon: <Activity size={22} />,
        color: 'green',
        badge: 'Revenue',
        gradient: 'linear-gradient(135deg, #10b981, #047857)'
      },
      {
        title: 'Invoice due',
        value: loading ? spinner : formatCurrency(d.invoice_due || 0),
        icon: <FileText size={22} />,
        color: 'orange',
        badge: 'Receivables',
        gradient: 'linear-gradient(135deg, #f59e0b, #b45309)'
      },
      {
        title: 'Total Sell Return',
        value: loading ? spinner : formatCurrency(d.total_sell_return || 0),
        icon: <RotateCcw size={22} />,
        color: 'red',
        badge: 'Returns',
        gradient: 'linear-gradient(135deg, #ef4444, #b91c1c)'
      },
      {
        title: 'Total purchase',
        value: loading ? spinner : formatCurrency(d.total_purchase || 0),
        icon: <ShoppingBag size={22} />,
        color: 'purple',
        badge: 'Purchases',
        gradient: 'linear-gradient(135deg, #8b5cf6, #6d28d9)'
      },
      {
        title: 'Purchase due',
        value: loading ? spinner : formatCurrency(d.purchase_due || 0),
        icon: <Clock size={22} />,
        color: 'yellow',
        badge: 'Payables',
        gradient: 'linear-gradient(135deg, #eab308, #a16207)'
      },
      {
        title: 'Total Purchase Return',
        value: loading ? spinner : formatCurrency(d.total_purchase_return || 0),
        icon: <Receipt size={22} />,
        color: 'pink',
        badge: 'P-Returns',
        gradient: 'linear-gradient(135deg, #ec4899, #be185d)'
      },
      {
        title: 'Expense',
        value: loading ? spinner : formatCurrency(d.total_expense || 0),
        icon: <CreditCard size={22} />,
        color: 'gray',
        badge: 'Outflow',
        gradient: 'linear-gradient(135deg, #64748b, #334155)'
      },
    ];
  };

  // Fetch product stock alerts
  const fetchStockAlerts = async () => {
    if (!apiService.isTokenValid()) return;
    try {
      setStockAlertLoading(true);
      const response = await apiService.getProductStockAlert({
        location_id: stockAlertLocation === 'all' ? null : stockAlertLocation
      });
      setStockAlerts(response?.data || []);
    } catch (error) {
      /* console.error('Error fetching stock alerts:', error); */
      setStockAlerts([]);
    } finally {
      setStockAlertLoading(false);
    }
  };

  useEffect(() => {
    if (locationsLoaded) fetchStockAlerts();
  }, [stockAlertLocation, locationsLoaded]);

  return (
    <div className="etpos-home">
      {/* Modern Hero Welcome Section */}
      <div className="welcome-section">
        <div className="welcome-banner-content">
          <div className="welcome-user-info">
            <div className="welcome-avatar-ring">
              <span className="welcome-avatar-icon">👋</span>
            </div>
            <div>
              <h2 className="welcome-heading">
                Welcome back, <span className="user-name-highlight">{currentUser ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || currentUser.username : 'User'}</span>
              </h2>
              <p className="welcome-subtext">Real-time breakdown of your sales, purchases, dues, and inventory alerts.</p>
            </div>
          </div>

          <div className="controls-row">
            <div className="location-selector">
              <label><MapPin size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Select location</label>
              <select value={selectedLocationId} onChange={(e) => handleTopLocationChange(e.target.value)}>
                <option value="all">📍 All Locations</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>📍 {loc.name}</option>
                ))}
              </select>
            </div>
            <div className="location-selector">
              <label><Calendar size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Select date range</label>
              <DateFilter
                onDateChange={(range) => setDateRange({ start: range.start, end: range.end })}
                initialStartDate={dateRange.start}
                initialEndDate={dateRange.end}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="metrics-grid">
        {getSalesMetrics().map((metric, index) => (
          <div key={index} className={`metric-card metric-${metric.color}`}>
            <div className="metric-icon-box" style={{ background: metric.gradient }}>
              {metric.icon}
            </div>
            <div className="metric-content">
              <div className="metric-header-row">
                <span className="metric-title">{metric.title}</span>
                {metric.badge && <span className="metric-tag">{metric.badge}</span>}
              </div>
              <h3 className="metric-value">{metric.value}</h3>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        <div className="charts-row">
          <div className="chart-card">
            <div className="chart-card-header">
              <div className="chart-header-title">
                <div className="chart-title-icon chart-icon-blue">
                  <TrendingUp size={18} />
                </div>
                <div>
                  <h3>Sales Last 30 Days</h3>
                  <p className="chart-subtitle">Daily sales velocity & performance graph</p>
                </div>
              </div>
            </div>
            <div className="chart-container" style={{ height: '300px', marginTop: '1rem' }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={salesChartData.length > 0 ? salesChartData : [{ name: 'No data', sales: 0 }]} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.45} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(226, 232, 240, 0.7)" />
                  <XAxis dataKey="name" stroke="#64748b" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis stroke="#64748b" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(val) => `$${val}`} />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)', background: '#ffffff' }} />
                  <Area type="monotone" dataKey="sales" stroke="#2563eb" strokeWidth={3} fillOpacity={1} fill="url(#colorSales)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="chart-card">
            <div className="chart-card-header">
              <div className="chart-header-title">
                <div className="chart-title-icon chart-icon-green">
                  <Activity size={18} />
                </div>
                <div>
                  <h3>Sales Current Financial Year</h3>
                  <p className="chart-subtitle">Monthly total revenue breakdown</p>
                </div>
              </div>
            </div>
            <div className="chart-container" style={{ height: '300px', marginTop: '1rem' }}>
              {fyChartLoading ? (
                <div className="chart-loading">
                  <div className="spinner-dot"></div>
                  <span>Loading performance chart...</span>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={fyChartData.length ? fyChartData : [{ name: 'No data', sales: 0 }]} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorFySales" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.9} />
                        <stop offset="95%" stopColor="#059669" stopOpacity={0.6} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(226, 232, 240, 0.7)" />
                    <XAxis dataKey="name" stroke="#64748b" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis stroke="#64748b" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${Number(v).toLocaleString()}`} />
                    <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)', background: '#ffffff' }} />
                    <Bar dataKey="sales" fill="url(#colorFySales)" radius={[8, 8, 0, 0]} barSize={36} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tables Section */}
      <div className="tables-section">
        {/* Sales Payment Due Table */}
        <div className="table-card">
          <div className="table-header">
            <div className="table-title-group">
              <div className="table-badge-icon icon-orange"><FileText size={16} /></div>
              <h3>Sales Payment Due</h3>
            </div>
            <div className="table-controls">
              <div className="search-box-custom">
                <Search size={13} color="#64748b" />
                <input
                  type="text"
                  placeholder="Search customer / invoice..."
                  value={salesSearch}
                  onChange={(e) => setSalesSearch(e.target.value)}
                />
              </div>
              <div className="location-select-wrap">
                <select value={salesDuesLocation} onChange={(e) => setSalesDuesLocation(e.target.value)}>
                  <option value="all">All Locations</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '35%' }}>Customer</th>
                  <th style={{ width: '25%' }}>Invoice No</th>
                  <th style={{ width: '20%' }}>Due Amount</th>
                  <th style={{ width: '20%' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {duesLoading ? (
                  <tr>
                    <td colSpan="4" className="no-data">
                      <div className="table-loading-container">
                        <Loader2 className="spinner-icon" size={22} />
                        <span>Loading sales dues...</span>
                      </div>
                    </td>
                  </tr>
                ) : paginatedSales.length === 0 ? (
                  <tr><td colSpan="4" className="no-data">No sales payment dues found</td></tr>
                ) : (
                  paginatedSales.map((row, idx) => (
                    <tr key={idx}>
                      <td className="cell-truncate">{row.contact}</td>
                      <td className="cell-bold-code">{row.reference_no}</td>
                      <td className="cell-highlight-due">{formatCurrency(row.due_amount)}</td>
                      <td>
                        <button
                          className="btn-action-payment"
                          onClick={() => setPaymentModal({ open: true, type: 'sell', row })}
                        >
                          <Banknote size={13} /> Add payment
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {filteredSales.length > 0 && (
            <div className="table-footer">
              <span className="footer-entries-info">
                Showing {(salesPage - 1) * itemsPerPage + 1} to {Math.min(salesPage * itemsPerPage, filteredSales.length)} of {filteredSales.length} entries
              </span>
              <Pagination currentPage={salesPage} totalPages={salesTotalPages} onPageChange={setSalesPage} />
            </div>
          )}
        </div>

        {/* Purchase Payment Due Table */}
        <div className="table-card">
          <div className="table-header">
            <div className="table-title-group">
              <div className="table-badge-icon icon-purple"><ShoppingBag size={16} /></div>
              <h3>Purchase Payment Due</h3>
            </div>
            <div className="table-controls">
              <div className="search-box-custom">
                <Search size={13} color="#64748b" />
                <input
                  type="text"
                  placeholder="Search supplier / ref..."
                  value={purchaseSearch}
                  onChange={(e) => setPurchaseSearch(e.target.value)}
                />
              </div>
              <div className="location-select-wrap">
                <select value={purchaseDuesLocation} onChange={(e) => setPurchaseDuesLocation(e.target.value)}>
                  <option value="all">All Locations</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '35%' }}>Supplier</th>
                  <th style={{ width: '25%' }}>Reference No</th>
                  <th style={{ width: '20%' }}>Due Amount</th>
                  <th style={{ width: '20%' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {duesLoading ? (
                  <tr>
                    <td colSpan="4" className="no-data">
                      <div className="table-loading-container">
                        <Loader2 className="spinner-icon" size={22} />
                        <span>Loading purchase dues...</span>
                      </div>
                    </td>
                  </tr>
                ) : paginatedPurchase.length === 0 ? (
                  <tr><td colSpan="4" className="no-data">No purchase payment dues found</td></tr>
                ) : (
                  paginatedPurchase.map((row, idx) => (
                    <tr key={idx}>
                      <td className="cell-truncate">{row.contact}</td>
                      <td className="cell-bold-code">{row.reference_no}</td>
                      <td className="cell-highlight-due">{formatCurrency(row.due_amount)}</td>
                      <td>
                        <button
                          className="btn-action-payment"
                          onClick={() => setPaymentModal({ open: true, type: 'purchase', row })}
                        >
                          <Banknote size={13} /> Add payment
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {filteredPurchase.length > 0 && (
            <div className="table-footer">
              <span className="footer-entries-info">
                Showing {(purchasePage - 1) * itemsPerPage + 1} to {Math.min(purchasePage * itemsPerPage, filteredPurchase.length)} of {filteredPurchase.length} entries
              </span>
              <Pagination currentPage={purchasePage} totalPages={purchaseTotalPages} onPageChange={setPurchasePage} />
            </div>
          )}
        </div>
      </div>

      {/* Check Payments Table */}
      <div className="table-card">
        <div className="table-header">
          <div className="table-title-group">
            <div className="table-badge-icon icon-blue"><CheckCircle2 size={16} /></div>
            <h3>Check Payment Dues</h3>
          </div>
          <div className="table-controls">
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button
                className="export-btn"
                onClick={() => setOpenExportDropdown(openExportDropdown === 'chequePayments' ? null : 'chequePayments')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export ▾
              </button>
              {openExportDropdown === 'chequePayments' && (
                <div className="export-dropdown-menu">
                  <button onClick={() => { exportToCSV(['Customer', 'Invoice No', 'Check Number', 'Expiry Date', 'Submission Date', 'Status'], filteredChequePayments.map(p => [p.customer || '--', p.invoice_no || '--', p.cheque_number || '--', formatDate(p.cheque_expiry_date), formatDate(p.bank_submission_date), p.status || 'Pending']), 'Check_Payment_Dues'); setOpenExportDropdown(null); }}><i className="fa fa-file-csv"></i> Export CSV</button>
                  <button onClick={() => { exportToExcel(['Customer', 'Invoice No', 'Check Number', 'Expiry Date', 'Submission Date', 'Status'], filteredChequePayments.map(p => [p.customer || '--', p.invoice_no || '--', p.cheque_number || '--', formatDate(p.cheque_expiry_date), formatDate(p.bank_submission_date), p.status || 'Pending']), 'Check_Payment_Dues'); setOpenExportDropdown(null); }}><i className="fa fa-file-excel"></i> Export Excel</button>
                  <button onClick={() => { printPage('Check Payment Dues', ['Customer', 'Invoice No', 'Check Number', 'Expiry Date', 'Submission Date', 'Status'], filteredChequePayments.map(p => [p.customer || '--', p.invoice_no || '--', p.cheque_number || '--', formatDate(p.cheque_expiry_date), formatDate(p.bank_submission_date), p.status || 'Pending'])); setOpenExportDropdown(null); }}><i className="fa fa-print"></i> Print</button>
                  <button onClick={() => { exportToPDF('Check Payment Dues', ['Customer', 'Invoice No', 'Check Number', 'Expiry Date', 'Submission Date', 'Status'], filteredChequePayments.map(p => [p.customer || '--', p.invoice_no || '--', p.cheque_number || '--', formatDate(p.cheque_expiry_date), formatDate(p.bank_submission_date), p.status || 'Pending']), 'Check_Payment_Dues'); setOpenExportDropdown(null); }}><i className="fa fa-file-pdf"></i> Export PDF</button>
                </div>
              )}
            </div>
            <div className="search-box-custom">
              <Search size={13} color="#64748b" />
              <input
                type="text"
                placeholder="Search check / customer..."
                value={chequePaymentsSearch}
                onChange={(e) => setChequePaymentsSearch(e.target.value)}
              />
            </div>
            <div className="location-select-wrap">
              <select value={chequePaymentsLocation} onChange={(e) => setChequePaymentsLocation(e.target.value)}>
                <option value="all">All Locations</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: '20%' }}>Customer</th>
                <th style={{ width: '15%' }}>Invoice No</th>
                <th style={{ width: '15%' }}>Check Number</th>
                <th style={{ width: '12%' }}>Expiry</th>
                <th style={{ width: '15%' }}>Submission</th>
                <th style={{ width: '13%' }}>Status</th>
                <th style={{ width: '10%' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {chequePaymentsLoading ? (
                <tr>
                  <td colSpan="7" className="no-data">
                    <div className="table-loading-container">
                      <Loader2 className="spinner-icon" size={22} />
                      <span>Loading check details...</span>
                    </div>
                  </td>
                </tr>
              ) : paginatedChequePayments.length === 0 ? (
                <tr><td colSpan="7" className="no-data">No check payment dues found</td></tr>
              ) : (
                paginatedChequePayments.map((p, i) => (
                  <tr key={p.id || i}>
                    <td className="cell-truncate">{p.customer}</td>
                    <td className="cell-bold-code">{p.invoice_no}</td>
                    <td className="cell-bold-code">{p.cheque_number}</td>
                    <td>{formatDate(p.cheque_expiry_date)}</td>
                    <td>{formatDate(p.bank_submission_date)}</td>
                    <td>
                      <div className="status-cell-wrapper">
                        <select
                          className={`status-select-pill ${p.status === 'Completed' ? 'status-completed' : 'status-pending'}`}
                          value={p.status || 'Pending'}
                          disabled={editingChequeId !== p.id}
                          onChange={async (e) => {
                            const newStatus = e.target.value;
                            try {
                              await apiService.updateChequeStatus(p.id, newStatus);
                              setChequePayments(prev => prev.map(cp => cp.id === p.id ? { ...cp, status: newStatus } : cp));
                              setEditingChequeId(null);
                            } catch (err) {
                              /* console.error('Failed to update cheque status:', err); */
                            }
                          }}
                        >
                          <option value="Pending">🟡 Pending</option>
                          <option value="Completed">🟢 Completed</option>
                        </select>
                        <button
                          onClick={() => setEditingChequeId(editingChequeId === p.id ? null : p.id)}
                          className="btn-edit-status"
                          title="Toggle edit status"
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path></svg>
                        </button>
                      </div>
                    </td>
                    <td>
                      <button
                        className="btn-action-view"
                        onClick={async () => {
                          if (!p.transaction_id) return;
                          try {
                            const saleData = await apiService.getSaleView(p.transaction_id);
                            let extractedSale = saleData;
                            if (saleData && saleData.data) {
                              extractedSale = Array.isArray(saleData.data) ? saleData.data[0] : saleData.data;
                            } else if (Array.isArray(saleData)) {
                              extractedSale = saleData[0];
                            }
                            setViewSaleModal({ open: true, sale: { apiData: extractedSale }, loading: false });
                          } catch (err) {
                            /* console.error('Failed to fetch sale details:', err); */
                            alert('Failed to load sale details');
                          }
                        }}
                      >
                        <Eye size={12} /> View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filteredChequePayments.length > 0 && (
          <div className="table-footer">
            <span className="footer-entries-info">
              Showing {(chequePaymentsPage - 1) * itemsPerPage + 1} to {Math.min(chequePaymentsPage * itemsPerPage, filteredChequePayments.length)} of {filteredChequePayments.length} entries
            </span>
            <Pagination currentPage={chequePaymentsPage} totalPages={chequePaymentsTotalPages} onPageChange={setChequePaymentsPage} />
          </div>
        )}
      </div>

      {/* Product Stock Alert */}
      <div className="table-card">
        <div className="table-header">
          <div className="table-title-group">
            <div className="table-badge-icon icon-red"><AlertTriangle size={16} /></div>
            <h3>Product Stock Alert</h3>
          </div>
          <div className="table-controls">
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button
                className="export-btn"
                onClick={() => setOpenExportDropdown(openExportDropdown === 'stockAlerts' ? null : 'stockAlerts')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export ▾
              </button>
              {openExportDropdown === 'stockAlerts' && (
                <div className="export-dropdown-menu">
                  <button onClick={() => { exportToCSV(['Product', 'Location', 'Current Stock'], filteredStock.map(a => [a.product || '--', a.location || '--', `${parseFloat(a.stock || 0).toFixed(quantityPrecision)} ${a.unit || ''}`]), 'Product_Stock_Alerts'); setOpenExportDropdown(null); }}><i className="fa fa-file-csv"></i> Export CSV</button>
                  <button onClick={() => { exportToExcel(['Product', 'Location', 'Current Stock'], filteredStock.map(a => [a.product || '--', a.location || '--', `${parseFloat(a.stock || 0).toFixed(quantityPrecision)} ${a.unit || ''}`]), 'Product_Stock_Alerts'); setOpenExportDropdown(null); }}><i className="fa fa-file-excel"></i> Export Excel</button>
                  <button onClick={() => { printPage('Product Stock Alerts', ['Product', 'Location', 'Current Stock'], filteredStock.map(a => [a.product || '--', a.location || '--', `${parseFloat(a.stock || 0).toFixed(quantityPrecision)} ${a.unit || ''}`])); setOpenExportDropdown(null); }}><i className="fa fa-print"></i> Print</button>
                  <button onClick={() => { exportToPDF('Product Stock Alerts', ['Product', 'Location', 'Current Stock'], filteredStock.map(a => [a.product || '--', a.location || '--', `${parseFloat(a.stock || 0).toFixed(quantityPrecision)} ${a.unit || ''}`]), 'Product_Stock_Alerts'); setOpenExportDropdown(null); }}><i className="fa fa-file-pdf"></i> Export PDF</button>
                </div>
              )}
            </div>
            <div className="search-box-custom">
              <Search size={13} color="#64748b" />
              <input
                type="text"
                placeholder="Search product..."
                value={stockSearch}
                onChange={(e) => setStockSearch(e.target.value)}
              />
            </div>
            <div className="location-select-wrap">
              <select value={stockAlertLocation} onChange={(e) => setStockAlertLocation(e.target.value)}>
                <option value="all">All Locations</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Location</th>
                <th>Current stock</th>
              </tr>
            </thead>
            <tbody>
              {stockAlertLoading ? (
                <tr>
                  <td colSpan="3" className="no-data">
                    <div className="table-loading-container">
                      <Loader2 className="spinner-icon" size={22} />
                      <span>Loading stock alerts...</span>
                    </div>
                  </td>
                </tr>
              ) : paginatedStock.length === 0 ? (
                <tr><td colSpan="3" className="no-data">No stock alerts found</td></tr>
              ) : (
                paginatedStock.map((alert, i) => (
                  <tr key={i}>
                    <td className="product-name">{alert.product}</td>
                    <td>{alert.location}</td>
                    <td>
                      <span className="stock-alert-pill">
                        {parseFloat(alert.stock || 0).toFixed(quantityPrecision)} {alert.unit}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filteredStock.length > 0 && (
          <div className="table-footer">
            <span className="footer-entries-info">
              Showing {(stockPage - 1) * itemsPerPage + 1} to {Math.min(stockPage * itemsPerPage, filteredStock.length)} of {filteredStock.length} entries
            </span>
            <Pagination currentPage={stockPage} totalPages={stockTotalPages} onPageChange={setStockPage} />
          </div>
        )}
      </div>

      {/* 1. Sales Order */}
      <div className="table-card" style={{ marginTop: '1.5rem' }}>
        <div className="table-header">
          <div className="table-title-group">
            <div className="table-badge-icon icon-yellow"><FileText size={16} /></div>
            <h3>Sales Order</h3>
          </div>
          <div className="table-controls">
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button
                className="export-btn"
                onClick={() => setOpenExportDropdown(openExportDropdown === 'salesOrders' ? null : 'salesOrders')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export ▾
              </button>
              {openExportDropdown === 'salesOrders' && (
                <div className="export-dropdown-menu">
                  <button onClick={() => { exportToCSV(['Date', 'Order No.', 'Customer', 'Mobile', 'Location', 'Status', 'Shipping Status', 'Remaining Qty', 'Added By'], filteredSalesOrders.map(r => [formatDate(r.transaction_date), r.invoice_no || r.ref_no || '--', r.contact?.name || r.customer || '--', r.contact?.mobile || '--', r.location?.name || r.location_name || '--', r.status || '--', r.shipping_status || '--', r.quantity_remaining || '--', r.created_by_name || r.user_full_name || '--']), 'Sales_Orders'); setOpenExportDropdown(null); }}><i className="fa fa-file-csv"></i> Export CSV</button>
                  <button onClick={() => { exportToExcel(['Date', 'Order No.', 'Customer', 'Mobile', 'Location', 'Status', 'Shipping Status', 'Remaining Qty', 'Added By'], filteredSalesOrders.map(r => [formatDate(r.transaction_date), r.invoice_no || r.ref_no || '--', r.contact?.name || r.customer || '--', r.contact?.mobile || '--', r.location?.name || r.location_name || '--', r.status || '--', r.shipping_status || '--', r.quantity_remaining || '--', r.created_by_name || r.user_full_name || '--']), 'Sales_Orders'); setOpenExportDropdown(null); }}><i className="fa fa-file-excel"></i> Export Excel</button>
                  <button onClick={() => { printPage('Sales Orders', ['Date', 'Order No.', 'Customer', 'Mobile', 'Location', 'Status', 'Shipping Status', 'Remaining Qty', 'Added By'], filteredSalesOrders.map(r => [formatDate(r.transaction_date), r.invoice_no || r.ref_no || '--', r.contact?.name || r.customer || '--', r.contact?.mobile || '--', r.location?.name || r.location_name || '--', r.status || '--', r.shipping_status || '--', r.quantity_remaining || '--', r.created_by_name || r.user_full_name || '--'])); setOpenExportDropdown(null); }}><i className="fa fa-print"></i> Print</button>
                  <button onClick={() => { exportToPDF('Sales Orders', ['Date', 'Order No.', 'Customer', 'Mobile', 'Location', 'Status', 'Shipping Status', 'Remaining Qty', 'Added By'], filteredSalesOrders.map(r => [formatDate(r.transaction_date), r.invoice_no || r.ref_no || '--', r.contact?.name || r.customer || '--', r.contact?.mobile || '--', r.location?.name || r.location_name || '--', r.status || '--', r.shipping_status || '--', r.quantity_remaining || '--', r.created_by_name || r.user_full_name || '--']), 'Sales_Orders'); setOpenExportDropdown(null); }}><i className="fa fa-file-pdf"></i> Export PDF</button>
                </div>
              )}
            </div>
            <div className="search-box-custom">
              <Search size={13} color="#64748b" />
              <input
                type="text"
                placeholder="Search..."
                value={salesOrdersSearch}
                onChange={(e) => setSalesOrdersSearch(e.target.value)}
              />
            </div>
            <div className="location-select-wrap">
              <select value={salesOrdersLocation} onChange={(e) => setSalesOrdersLocation(e.target.value)}>
                <option value="all">All Locations</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Action</th>
                <th>Date</th>
                <th>Order No.</th>
                <th>Customer name</th>
                <th>Contact Number</th>
                <th>Location</th>
                <th>Status</th>
                <th>Shipping Status</th>
                <th>Quantity Remaining</th>
                <th>Added By</th>
              </tr>
            </thead>
            <tbody>
              {salesOrdersLoading ? (
                <tr>
                  <td colSpan="10" className="no-data">
                    <div className="table-loading-container">
                      <Loader2 className="spinner-icon" size={22} />
                      <span>Loading sales orders...</span>
                    </div>
                  </td>
                </tr>
              ) : paginatedSalesOrders.length === 0 ? (
                <tr><td colSpan="10" className="no-data">No data available in table</td></tr>
              ) : (
                paginatedSalesOrders.map((r, i) => (
                  <tr key={i}>
                    <td>
                      <div className="action-dropdown-container" style={{ position: 'relative', display: 'inline-block' }}>
                        <button
                          className="actions-btn action-btn-trigger"
                          style={{
                            background: 'transparent',
                            color: '#0284c7',
                            border: '1px solid #0284c7',
                            borderRadius: '4px',
                            padding: '0.25rem 0.6rem',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.35rem',
                            transition: 'all 0.2s ease'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleActionMenu(`salesOrders-${r.id || i}`, e);
                          }}
                        >
                          Actions <span className="arrow-down">▼</span>
                        </button>
                        {openActionId === `salesOrders-${r.id || i}` && ReactDOM.createPortal(
                          <div
                            className="action-dropdown-menu"
                            style={{
                              position: 'fixed',
                              top: `${dropdownPosition.top}px`,
                              left: `${dropdownPosition.left}px`,
                              transform: dropdownPosition.openUpwards ? 'translateY(-100%)' : 'none',
                              zIndex: 999999,
                              background: '#ffffff',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15)',
                              minWidth: '170px',
                              padding: '0.35rem 0'
                            }}
                          >
                            <button className="dropdown-item" onClick={() => { setViewSaleModal({ open: true, sale: { apiData: r }, loading: false }); setOpenActionId(null); }}>
                              👁️ View
                            </button>
                            <button className="dropdown-item" onClick={() => { alert(`Edit Order: ${r.invoice_no || r.ref_no}`); setOpenActionId(null); }}>
                              ✏️ Edit
                            </button>
                            <button className="dropdown-item" onClick={() => { printPage('Sales Orders', ['Date', 'Order No.', 'Customer', 'Mobile', 'Location', 'Status', 'Shipping Status', 'Remaining Qty', 'Added By'], [[formatDate(r.transaction_date), r.invoice_no || r.ref_no || '--', r.contact?.name || r.customer || '--', r.contact?.mobile || '--', r.location?.name || r.location_name || '--', r.status || '--', r.shipping_status || '--', r.quantity_remaining || '--', r.created_by_name || r.user_full_name || '--']]); setOpenActionId(null); }}>
                              🖨️ Print Invoice
                            </button>
                            <button className="dropdown-item" onClick={() => { alert(`Packing Slip for Order: ${r.invoice_no || r.ref_no}`); setOpenActionId(null); }}>
                              📋 Packing Slip
                            </button>
                            <button className="dropdown-item" onClick={() => { setViewSaleModal({ open: true, sale: { apiData: r }, loading: false }); setOpenActionId(null); }}>
                              📜 Ledger
                            </button>
                          </div>,
                          document.body
                        )}
                      </div>
                    </td>
                    <td>{formatDate(r.transaction_date || r.created_at)}</td>
                    <td className="cell-bold-code">{r.invoice_no || r.ref_no || '--'}</td>
                    <td className="product-name">{r.contact?.name || r.customer_name || r.customer || '--'}</td>
                    <td>{r.mobile || r.contact?.mobile || r.contact_number || '--'}</td>
                    <td>{r.location?.name || r.location_name || r.location || '--'}</td>
                    <td>
                      <span className={`metric-tag ${r.status === 'final' || r.status === 'completed' ? 'status-completed' : 'status-pending'}`}>
                        {r.status || 'ordered'}
                      </span>
                    </td>
                    <td>
                      <span className="metric-tag">{r.shipping_status || '--'}</span>
                    </td>
                    <td>{r.quantity_remaining !== undefined ? r.quantity_remaining : '--'}</td>
                    <td>{r.created_by || r.created_by_name || r.added_by || r.user_full_name || '--'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filteredSalesOrders.length > 0 && (
          <div className="table-footer">
            <span className="footer-entries-info">
              Showing {(salesOrdersPage - 1) * itemsPerPage + 1} to {Math.min(salesOrdersPage * itemsPerPage, filteredSalesOrders.length)} of {filteredSalesOrders.length} entries
            </span>
            <Pagination currentPage={salesOrdersPage} totalPages={salesOrdersTotalPages} onPageChange={setSalesOrdersPage} />
          </div>
        )}
      </div>

      {/* 2. Purchase Requisition */}
      <div className="table-card" style={{ marginTop: '1.5rem' }}>
        <div className="table-header">
          <div className="table-title-group">
            <div className="table-badge-icon icon-orange"><FileText size={16} /></div>
            <h3>Purchase Requisition</h3>
          </div>
          <div className="table-controls">
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button
                className="export-btn"
                onClick={() => setOpenExportDropdown(openExportDropdown === 'purchaseReq' ? null : 'purchaseReq')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export ▾
              </button>
              {openExportDropdown === 'purchaseReq' && (
                <div className="export-dropdown-menu">
                  <button onClick={() => { exportToCSV(['Date', 'Reference No', 'Location', 'Status', 'Required by date', 'Added By'], filteredPurchaseReqs.map(r => [formatDate(r.transaction_date || r.created_at), r.ref_no || '--', r.location?.name || r.location_name || '--', r.status || '--', resolveRequiredByDate(r), resolveAddedBy(r)]), 'Purchase_Requisitions'); setOpenExportDropdown(null); }}><i className="fa fa-file-csv"></i> Export CSV</button>
                  <button onClick={() => { exportToExcel(['Date', 'Reference No', 'Location', 'Status', 'Required by date', 'Added By'], filteredPurchaseReqs.map(r => [formatDate(r.transaction_date || r.created_at), r.ref_no || '--', r.location?.name || r.location_name || '--', r.status || '--', resolveRequiredByDate(r), resolveAddedBy(r)]), 'Purchase_Requisitions'); setOpenExportDropdown(null); }}><i className="fa fa-file-excel"></i> Export Excel</button>
                  <button onClick={() => { printPage('Purchase Requisitions', ['Date', 'Reference No', 'Location', 'Status', 'Required by date', 'Added By'], filteredPurchaseReqs.map(r => [formatDate(r.transaction_date || r.created_at), r.ref_no || '--', r.location?.name || r.location_name || '--', r.status || '--', resolveRequiredByDate(r), resolveAddedBy(r)])); setOpenExportDropdown(null); }}><i className="fa fa-print"></i> Print</button>
                  <button onClick={() => { exportToPDF('Purchase Requisitions', ['Date', 'Reference No', 'Location', 'Status', 'Required by date', 'Added By'], filteredPurchaseReqs.map(r => [formatDate(r.transaction_date || r.created_at), r.ref_no || '--', r.location?.name || r.location_name || '--', r.status || '--', resolveRequiredByDate(r), resolveAddedBy(r)]), 'Purchase_Requisitions'); setOpenExportDropdown(null); }}><i className="fa fa-file-pdf"></i> Export PDF</button>
                </div>
              )}
            </div>
            <div className="search-box-custom">
              <Search size={13} color="#64748b" />
              <input
                type="text"
                placeholder="Search..."
                value={purchaseReqSearch}
                onChange={(e) => setPurchaseReqSearch(e.target.value)}
              />
            </div>
            <div className="location-select-wrap">
              <select value={purchaseReqLocation} onChange={(e) => setPurchaseReqLocation(e.target.value)}>
                <option value="all">All Locations</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Action</th>
                <th>Date</th>
                <th>Reference No</th>
                <th>Location</th>
                <th>Status</th>
                <th>Required by date</th>
                <th>Added By</th>
              </tr>
            </thead>
            <tbody>
              {purchaseReqLoading ? (
                <tr>
                  <td colSpan="7" className="no-data">
                    <div className="table-loading-container">
                      <Loader2 className="spinner-icon" size={22} />
                      <span>Loading purchase requisitions...</span>
                    </div>
                  </td>
                </tr>
              ) : paginatedPurchaseReqs.length === 0 ? (
                <tr><td colSpan="7" className="no-data">No data available in table</td></tr>
              ) : (
                paginatedPurchaseReqs.map((r, i) => (
                  <tr key={i}>
                    <td>
                      <div className="action-dropdown-container" style={{ position: 'relative', display: 'inline-block' }}>
                        <button
                          className="actions-btn action-btn-trigger"
                          style={{
                            background: 'transparent',
                            color: '#0284c7',
                            border: '1px solid #0284c7',
                            borderRadius: '4px',
                            padding: '0.25rem 0.6rem',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.35rem',
                            transition: 'all 0.2s ease'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleActionMenu(`purchaseReq-${r.id || i}`, e);
                          }}
                        >
                          Actions <span className="arrow-down">▼</span>
                        </button>
                        {openActionId === `purchaseReq-${r.id || i}` && ReactDOM.createPortal(
                          <div
                            className="action-dropdown-menu"
                            style={{
                              position: 'fixed',
                              top: `${dropdownPosition.top}px`,
                              left: `${dropdownPosition.left}px`,
                              transform: dropdownPosition.openUpwards ? 'translateY(-100%)' : 'none',
                              zIndex: 999999,
                              background: '#ffffff',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15)',
                              minWidth: '150px',
                              padding: '0.35rem 0'
                            }}
                          >
                            <button className="dropdown-item" onClick={() => handleViewPurchaseReq(r)}>
                              👁️ View
                            </button>
                            <button className="dropdown-item delete-item" onClick={() => handleDeletePurchaseReq(r.id)} style={{ color: '#ef4444' }}>
                              🗑️ Delete
                            </button>
                          </div>,
                          document.body
                        )}
                      </div>
                    </td>
                    <td>{formatDateTime(r.transaction_date || r.created_at)}</td>
                    <td className="cell-bold-code">{r.ref_no || '--'}</td>
                    <td>{r.location?.name || r.location_name || r.location || '--'}</td>
                    <td>
                      <span className="metric-tag status-pending">{r.status || 'pending'}</span>
                    </td>
                    <td>{resolveRequiredByDate(r)}</td>
                    <td>{resolveAddedBy(r)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filteredPurchaseReqs.length > 0 && (
          <div className="table-footer">
            <span className="footer-entries-info">
              Showing {(purchaseReqPage - 1) * itemsPerPage + 1} to {Math.min(purchaseReqPage * itemsPerPage, filteredPurchaseReqs.length)} of {filteredPurchaseReqs.length} entries
            </span>
            <Pagination currentPage={purchaseReqPage} totalPages={purchaseReqTotalPages} onPageChange={setPurchaseReqPage} />
          </div>
        )}
      </div>

      {/* 3. Purchase Order */}
      <div className="table-card" style={{ marginTop: '1.5rem' }}>
        <div className="table-header">
          <div className="table-title-group">
            <div className="table-badge-icon icon-purple"><ShoppingBag size={16} /></div>
            <h3>Purchase Order</h3>
          </div>
          <div className="table-controls">
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button
                className="export-btn"
                onClick={() => setOpenExportDropdown(openExportDropdown === 'purchaseOrders' ? null : 'purchaseOrders')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export ▾
              </button>
              {openExportDropdown === 'purchaseOrders' && (
                <div className="export-dropdown-menu">
                  <button onClick={() => { exportToCSV(['Date', 'Reference No', 'Location', 'Supplier', 'Status', 'Remaining Qty', 'Added By'], filteredPurchaseOrders.map(r => [formatDate(r.transaction_date || r.created_at), r.ref_no || '--', r.location?.name || r.location_name || '--', r.supplier?.name || r.contact?.name || '--', r.status || '--', r.quantity_remaining || '--', r.created_by_name || r.added_by || '--']), 'Purchase_Orders'); setOpenExportDropdown(null); }}><i className="fa fa-file-csv"></i> Export CSV</button>
                  <button onClick={() => { exportToExcel(['Date', 'Reference No', 'Location', 'Supplier', 'Status', 'Remaining Qty', 'Added By'], filteredPurchaseOrders.map(r => [formatDate(r.transaction_date || r.created_at), r.ref_no || '--', r.location?.name || r.location_name || '--', r.supplier?.name || r.contact?.name || '--', r.status || '--', r.quantity_remaining || '--', r.created_by_name || r.added_by || '--']), 'Purchase_Orders'); setOpenExportDropdown(null); }}><i className="fa fa-file-excel"></i> Export Excel</button>
                  <button onClick={() => { printPage('Purchase Orders', ['Date', 'Reference No', 'Location', 'Supplier', 'Status', 'Remaining Qty', 'Added By'], filteredPurchaseOrders.map(r => [formatDate(r.transaction_date || r.created_at), r.ref_no || '--', r.location?.name || r.location_name || '--', r.supplier?.name || r.contact?.name || '--', r.status || '--', r.quantity_remaining || '--', r.created_by_name || r.added_by || '--'])); setOpenExportDropdown(null); }}><i className="fa fa-print"></i> Print</button>
                  <button onClick={() => { exportToPDF('Purchase Orders', ['Date', 'Reference No', 'Location', 'Supplier', 'Status', 'Remaining Qty', 'Added By'], filteredPurchaseOrders.map(r => [formatDate(r.transaction_date || r.created_at), r.ref_no || '--', r.location?.name || r.location_name || '--', r.supplier?.name || r.contact?.name || '--', r.status || '--', r.quantity_remaining || '--', r.created_by_name || r.added_by || '--']), 'Purchase_Orders'); setOpenExportDropdown(null); }}><i className="fa fa-file-pdf"></i> Export PDF</button>
                </div>
              )}
            </div>
            <div className="search-box-custom">
              <Search size={13} color="#64748b" />
              <input
                type="text"
                placeholder="Search..."
                value={purchaseOrdersSearch}
                onChange={(e) => setPurchaseOrdersSearch(e.target.value)}
              />
            </div>
            <div className="location-select-wrap">
              <select value={purchaseOrdersLocation} onChange={(e) => setPurchaseOrdersLocation(e.target.value)}>
                <option value="all">All Locations</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Action</th>
                <th>Date</th>
                <th>Reference No</th>
                <th>Location</th>
                <th>Supplier</th>
                <th>Status</th>
                <th>Quantity Remaining</th>
                <th>Added By</th>
              </tr>
            </thead>
            <tbody>
              {purchaseOrdersLoading ? (
                <tr>
                  <td colSpan="8" className="no-data">
                    <div className="table-loading-container">
                      <Loader2 className="spinner-icon" size={22} />
                      <span>Loading purchase orders...</span>
                    </div>
                  </td>
                </tr>
              ) : paginatedPurchaseOrders.length === 0 ? (
                <tr><td colSpan="8" className="no-data">No data available in table</td></tr>
              ) : (
                paginatedPurchaseOrders.map((r, i) => (
                  <tr key={i}>
                    <td>
                      <div className="action-dropdown-container" style={{ position: 'relative', display: 'inline-block' }}>
                        <button
                          className="actions-btn action-btn-trigger"
                          style={{
                            background: 'transparent',
                            color: '#0284c7',
                            border: '1px solid #0284c7',
                            borderRadius: '4px',
                            padding: '0.25rem 0.6rem',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.35rem',
                            transition: 'all 0.2s ease'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleActionMenu(`purchaseOrders-${r.id || i}`, e);
                          }}
                        >
                          Actions <span className="arrow-down">▼</span>
                        </button>
                        {openActionId === `purchaseOrders-${r.id || i}` && ReactDOM.createPortal(
                          <div
                            className="action-dropdown-menu"
                            style={{
                              position: 'fixed',
                              top: `${dropdownPosition.top}px`,
                              left: `${dropdownPosition.left}px`,
                              transform: dropdownPosition.openUpwards ? 'translateY(-100%)' : 'none',
                              zIndex: 999999,
                              background: '#ffffff',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15)',
                              minWidth: '175px',
                              padding: '0.35rem 0'
                            }}
                          >
                            <button className="dropdown-item" onClick={() => handleViewPO(r)}>
                              👁️ View
                            </button>
                            <button className="dropdown-item" onClick={() => handlePrintPO(r)}>
                              🖨️ Print
                            </button>
                            <button className="dropdown-item" onClick={() => handleDownloadPOPdf(r)}>
                              📄 Download pdf
                            </button>
                            <button className="dropdown-item" onClick={() => { setOpenActionId(null); navigate(`/purchases/order/${r.id}/edit`); }}>
                              ✏️ Edit
                            </button>
                            <button className="dropdown-item delete-item" onClick={() => handleDeletePO(r.id)} style={{ color: '#ef4444' }}>
                              🗑️ Delete
                            </button>
                            <button className="dropdown-item" onClick={() => { setEditShippingModal({ open: true, sale: r }); setOpenActionId(null); }}>
                              🚚 Edit Shipping
                            </button>
                            <button className="dropdown-item" onClick={() => {
                              const docPath = r.document_path || (r.document ? `/uploads/documents/${r.document}` : null);
                              if (docPath) {
                                const link = document.createElement('a');
                                link.href = docPath;
                                link.download = r.document_name || r.document || 'document';
                                link.click();
                              } else {
                                alert('No document attached');
                              }
                              setOpenActionId(null);
                            }}>
                              📥 Download Document
                            </button>
                            <button className="dropdown-item" onClick={() => {
                              const docPath = r.document_path || (r.document ? `/uploads/documents/${r.document}` : null);
                              if (docPath) {
                                window.open(docPath, '_blank');
                              } else {
                                alert('No document attached');
                              }
                              setOpenActionId(null);
                            }}>
                              👁️ View Document
                            </button>
                            <button className="dropdown-item" onClick={() => { setNotificationModal({ open: true, sale: r }); setOpenActionId(null); }}>
                              ✉️ Send Notification
                            </button>
                          </div>,
                          document.body
                        )}
                      </div>
                    </td>
                    <td>{formatDate(r.transaction_date || r.created_at)}</td>
                    <td className="cell-bold-code">{r.ref_no || '--'}</td>
                    <td>{r.location?.name || r.location_name || (typeof r.location === 'string' ? r.location : '') || '--'}</td>
                    <td className="product-name">{resolveSupplierName(r)}</td>
                    <td>
                      <span className="metric-tag" style={{ background: 'rgba(6, 182, 212, 0.12)', color: '#0891b2', border: '1px solid rgba(6, 182, 212, 0.3)' }}>
                        {r.status || 'Ordered'}
                      </span>
                    </td>
                    <td>{(r.po_qty_remaining !== undefined && r.po_qty_remaining !== null) ? r.po_qty_remaining : ((r.quantity_remaining !== undefined && r.quantity_remaining !== null) ? r.quantity_remaining : '--')}</td>
                    <td>{r.created_by_name || r.added_by || r.user_full_name || '--'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filteredPurchaseOrders.length > 0 && (
          <div className="table-footer">
            <span className="footer-entries-info">
              Showing {(purchaseOrdersPage - 1) * itemsPerPage + 1} to {Math.min(purchaseOrdersPage * itemsPerPage, filteredPurchaseOrders.length)} of {filteredPurchaseOrders.length} entries
            </span>
            <Pagination currentPage={purchaseOrdersPage} totalPages={purchaseOrdersTotalPages} onPageChange={setPurchaseOrdersPage} />
          </div>
        )}
      </div>

      {/* 4. Pending Shipments */}
      <div className="table-card" style={{ marginTop: '1.5rem' }}>
        <div className="table-header">
          <div className="table-title-group">
            <div className="table-badge-icon icon-orange"><TrendingUp size={16} /></div>
            <h3>Pending Shipments</h3>
          </div>
          <div className="table-controls">
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button
                className="export-btn"
                onClick={() => setOpenExportDropdown(openExportDropdown === 'pendingShipments' ? null : 'pendingShipments')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export ▾
              </button>
              {openExportDropdown === 'pendingShipments' && (
                <div className="export-dropdown-menu">
                  <button onClick={() => { exportToCSV(['Date', 'Invoice No.', 'Customer', 'Mobile', 'Location', 'Shipping Status', 'Payment Status'], filteredPendingShipments.map(r => [formatDate(r.transaction_date || r.created_at), r.invoice_no || r.ref_no || '--', r.contact?.name || r.customer || '--', r.contact?.mobile || '--', r.location?.name || r.location_name || '--', r.shipping_status || '--', r.payment_status || '--']), 'Pending_Shipments'); setOpenExportDropdown(null); }}><i className="fa fa-file-csv"></i> Export CSV</button>
                  <button onClick={() => { exportToExcel(['Date', 'Invoice No.', 'Customer', 'Mobile', 'Location', 'Shipping Status', 'Payment Status'], filteredPendingShipments.map(r => [formatDate(r.transaction_date || r.created_at), r.invoice_no || r.ref_no || '--', r.contact?.name || r.customer || '--', r.contact?.mobile || '--', r.location?.name || r.location_name || '--', r.shipping_status || '--', r.payment_status || '--']), 'Pending_Shipments'); setOpenExportDropdown(null); }}><i className="fa fa-file-excel"></i> Export Excel</button>
                  <button onClick={() => { printPage('Pending Shipments', ['Date', 'Invoice No.', 'Customer', 'Mobile', 'Location', 'Shipping Status', 'Payment Status'], filteredPendingShipments.map(r => [formatDate(r.transaction_date || r.created_at), r.invoice_no || r.ref_no || '--', r.contact?.name || r.customer || '--', r.contact?.mobile || '--', r.location?.name || r.location_name || '--', r.shipping_status || '--', r.payment_status || '--'])); setOpenExportDropdown(null); }}><i className="fa fa-print"></i> Print</button>
                  <button onClick={() => { exportToPDF('Pending Shipments', ['Date', 'Invoice No.', 'Customer', 'Mobile', 'Location', 'Shipping Status', 'Payment Status'], filteredPendingShipments.map(r => [formatDate(r.transaction_date || r.created_at), r.invoice_no || r.ref_no || '--', r.contact?.name || r.customer || '--', r.contact?.mobile || '--', r.location?.name || r.location_name || '--', r.shipping_status || '--', r.payment_status || '--']), 'Pending_Shipments'); setOpenExportDropdown(null); }}><i className="fa fa-file-pdf"></i> Export PDF</button>
                </div>
              )}
            </div>
            <div className="search-box-custom">
              <Search size={13} color="#64748b" />
              <input
                type="text"
                placeholder="Search..."
                value={pendingShipmentsSearch}
                onChange={(e) => setPendingShipmentsSearch(e.target.value)}
              />
            </div>
            <div className="location-select-wrap">
              <select value={pendingShipmentsLocation} onChange={(e) => setPendingShipmentsLocation(e.target.value)}>
                <option value="all">All Locations</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Action</th>
                <th>Date</th>
                <th>Invoice No.</th>
                <th>Customer name</th>
                <th>Contact Number</th>
                <th>Location</th>
                <th>Shipping Status</th>
                <th>Payment Status</th>
              </tr>
            </thead>
            <tbody>
              {pendingShipmentsLoading ? (
                <tr>
                  <td colSpan="8" className="no-data">
                    <div className="table-loading-container">
                      <Loader2 className="spinner-icon" size={22} />
                      <span>Loading pending shipments...</span>
                    </div>
                  </td>
                </tr>
              ) : paginatedPendingShipments.length === 0 ? (
                <tr><td colSpan="8" className="no-data">No data available in table</td></tr>
              ) : (
                paginatedPendingShipments.map((r, i) => (
                  <tr key={i}>
                    <td>
                      <div className="action-dropdown-container" style={{ position: 'relative', display: 'inline-block' }}>
                        <button
                          className="actions-btn action-btn-trigger"
                          style={{
                            background: 'transparent',
                            color: '#0284c7',
                            border: '1px solid #0284c7',
                            borderRadius: '4px',
                            padding: '0.25rem 0.6rem',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.35rem',
                            transition: 'all 0.2s ease'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleActionMenu(`pendingShipments-${r.id || i}`, e);
                          }}
                        >
                          Actions <span className="arrow-down">▼</span>
                        </button>
                        {openActionId === `pendingShipments-${r.id || i}` && ReactDOM.createPortal(
                          <div
                            className="action-dropdown-menu"
                            style={{
                              position: 'fixed',
                              top: `${dropdownPosition.top}px`,
                              left: `${dropdownPosition.left}px`,
                              transform: dropdownPosition.openUpwards ? 'translateY(-100%)' : 'none',
                              zIndex: 999999,
                              background: '#ffffff',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15)',
                              minWidth: '185px',
                              padding: '0.35rem 0'
                            }}
                          >
                            <button className="dropdown-item" onClick={() => { setViewSaleModal({ open: true, sale: { apiData: r }, loading: false }); setOpenActionId(null); }}>
                              👁️ View
                            </button>
                            <button className="dropdown-item" onClick={() => { setEditShippingModal({ open: true, sale: r }); setOpenActionId(null); }}>
                              🚚 Edit Shipping
                            </button>
                            <button className="dropdown-item" onClick={() => {
                              const invUrl = r.invoice_url || r.invoice_link || r.apiData?.invoice_url;
                              if (invUrl) {
                                window.open(invUrl, '_blank');
                              } else {
                                const saleId = r.id || r.apiData?.id;
                                if (saleId) {
                                  const host = window.location.host.includes(':3000') ? window.location.host.replace(':3000', '') : window.location.host;
                                  window.open(`${window.location.protocol}//${host}/invoice/${saleId}`, '_blank');
                                } else {
                                  setViewSaleModal({ open: true, sale: { apiData: r }, loading: false });
                                }
                              }
                              setOpenActionId(null);
                            }}>
                              🖨️ Print Invoice
                            </button>
                            <button className="dropdown-item" onClick={() => {
                              const saleId = r.id || r.apiData?.id;
                              if (saleId) {
                                const host = window.location.host.includes(':3000') ? window.location.host.replace(':3000', '') : window.location.host;
                                window.open(`${window.location.protocol}//${host}/sells/${saleId}/packing-slip`, '_blank');
                              } else {
                                setViewSaleModal({ open: true, sale: { apiData: r }, loading: false });
                              }
                              setOpenActionId(null);
                            }}>
                              📋 Packing Slip
                            </button>
                            <button className="dropdown-item" onClick={() => {
                              const saleId = r.id || r.apiData?.id;
                              if (saleId) {
                                const host = window.location.host.includes(':3000') ? window.location.host.replace(':3000', '') : window.location.host;
                                window.open(`${window.location.protocol}//${host}/sells/${saleId}/delivery-note`, '_blank');
                              } else {
                                setViewSaleModal({ open: true, sale: { apiData: r }, loading: false });
                              }
                              setOpenActionId(null);
                            }}>
                              📄 Delivery Note
                            </button>
                            <div style={{ height: '1px', background: '#e2e8f0', margin: '4px 0' }}></div>
                            <button className="dropdown-item" onClick={() => { setNotificationModal({ open: true, sale: r }); setOpenActionId(null); }}>
                              ✉️ New Sale Notification
                            </button>
                            <button className="dropdown-item" onClick={() => { setLedgerModal({ open: true, sale: r }); setOpenActionId(null); }}>
                              📜 Ledger
                            </button>
                          </div>,
                          document.body
                        )}
                      </div>
                    </td>
                    <td>{formatDate(r.transaction_date || r.created_at)}</td>
                    <td className="cell-bold-code">{r.invoice_no || r.ref_no || '--'}</td>
                    <td className="product-name">{r.contact?.name || r.customer || '--'}</td>
                    <td>{r.contact?.mobile || r.contact_number || '--'}</td>
                    <td>{r.location?.name || r.location_name || (typeof r.location === 'string' ? r.location : '') || '--'}</td>
                    <td>
                      <span className="metric-tag status-pending">{r.shipping_status || 'ordered'}</span>
                    </td>
                    <td>
                      <span className={`metric-tag ${r.payment_status === 'paid' ? 'status-completed' : 'status-pending'}`}>
                        {r.payment_status || 'due'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filteredPendingShipments.length > 0 && (
          <div className="table-footer">
            <span className="footer-entries-info">
              Showing {(pendingShipmentsPage - 1) * itemsPerPage + 1} to {Math.min(pendingShipmentsPage * itemsPerPage, filteredPendingShipments.length)} of {filteredPendingShipments.length} entries
            </span>
            <Pagination currentPage={pendingShipmentsPage} totalPages={pendingShipmentsTotalPages} onPageChange={setPendingShipmentsPage} />
          </div>
        )}
      </div>

      {/* View Sale Modal */}
      {viewSaleModal.open && (
        <ViewSaleModal
          sale={viewSaleModal.sale}
          onClose={() => setViewSaleModal({ open: false, sale: null, loading: false })}
          onPackingSlip={() => { }}
          onPrintInvoice={() => { }}
        />
      )}

      {/* View Purchase Modal */}
      {viewPurchaseModal.open && (
        <ViewPurchaseModal
          purchase={viewPurchaseModal.purchase}
          isLoading={viewPurchaseModal.loading}
          onClose={() => setViewPurchaseModal({ open: false, purchase: null, loading: false })}
          onPrint={handlePrintPO}
        />
      )}

      {/* View Purchase Requisition Modal */}
      {viewPurchaseReqModal.open && (
        <div className="modal-overlay" onClick={() => setViewPurchaseReqModal({ open: false, data: null, loading: false })} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999999 }}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ background: '#ffffff', width: '90%', maxWidth: '750px', maxHeight: '90vh', overflowY: 'auto', borderRadius: '8px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', padding: 0 }}>
            <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>
                Purchase Requisition Details (#{viewPurchaseReqModal.data?.ref_no || ''})
              </h2>
              <button onClick={() => setViewPurchaseReqModal({ open: false, data: null, loading: false })} style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#64748b', cursor: 'pointer' }}>&times;</button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              {viewPurchaseReqModal.loading ? (
                <div className="table-loading-container">
                  <Loader2 className="spinner-icon" size={22} />
                  <span>Loading requisition details...</span>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.875rem' }}>
                  <div><strong>Reference No:</strong> #{viewPurchaseReqModal.data?.ref_no || '--'}</div>
                  <div><strong>Date:</strong> {formatDate(viewPurchaseReqModal.data?.transaction_date || viewPurchaseReqModal.data?.created_at)}</div>
                  <div><strong>Location:</strong> {viewPurchaseReqModal.data?.location_name || viewPurchaseReqModal.data?.location?.name || '--'}</div>
                  <div><strong>Status:</strong> <span className="metric-tag" style={{ background: 'rgba(6, 182, 212, 0.12)', color: '#0891b2', border: '1px solid rgba(6, 182, 212, 0.3)' }}>{viewPurchaseReqModal.data?.status || 'Ordered'}</span></div>
                  <div><strong>Required by Date:</strong> {viewPurchaseReqModal.data?.delivery_date ? formatDate(viewPurchaseReqModal.data.delivery_date) : '--'}</div>
                  <div><strong>Added By:</strong> {viewPurchaseReqModal.data?.created_by_name || viewPurchaseReqModal.data?.added_by || '--'}</div>
                </div>
              )}
            </div>
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', background: '#f8fafc', borderRadius: '0 0 8px 8px' }}>
              <button onClick={() => setViewPurchaseReqModal({ open: false, data: null, loading: false })} style={{ background: '#334155', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '0.5rem 1.25rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Shipping Modal */}
      {editShippingModal.open && editShippingModal.sale && (
        <EditShippingModal
          sale={editShippingModal.sale}
          onClose={() => setEditShippingModal({ open: false, sale: null })}
          onUpdate={() => {
            reloadAllDashboardTables();
          }}
        />
      )}

      {/* Send Notification Modal */}
      {notificationModal.open && notificationModal.sale && (
        <SendNotificationModal
          sale={notificationModal.sale}
          onClose={() => setNotificationModal({ open: false, sale: null })}
        />
      )}

      {/* Ledger Modal */}
      {ledgerModal.open && ledgerModal.sale && (
        <LedgerModal
          sale={ledgerModal.sale}
          onClose={() => setLedgerModal({ open: false, sale: null })}
        />
      )}

      {/* Add Payment Modal */}
      {paymentModal.open && paymentModal.row && (
        <AddPaymentModal
          sale={{
            id: paymentModal.row.id,
            invoice_no: paymentModal.type === 'sell' ? paymentModal.row.reference_no : undefined,
            ref_no: paymentModal.type === 'purchase' ? paymentModal.row.reference_no : undefined,
            final_total: paymentModal.row.final_total || paymentModal.row.due_amount,
            payment_due: paymentModal.row.due_amount,
            contact: { name: paymentModal.row.contact },
          }}
          invoice={paymentModal.row}
          onClose={() => setPaymentModal({ open: false, type: null, row: null })}
          onSuccess={() => {
            setPaymentModal({ open: false, type: null, row: null });
            fetchDuesTables();
          }}
        />
      )}
    </div>
  );
};

export default Home;
